import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import {
  loadedSelectionHeaderState,
  manageDialogFetchSize,
  mergeLoadedSelection,
  pageClientRows,
} from '../../../../../lib/clientSelectionList';
import type { TargetingClient } from '../../../api';
import type { LiverampSegment } from '../../../model';
import { formatLiverampCpm } from '../TargetingForm.helpers';

const liverampPageSizes = [5, 10, 25, 50, 100] as const;
const emptyLiverampSegments: LiverampSegment[] = [];
const liverampLookup = { page: 0, size: manageDialogFetchSize };

export function ManageLiverampSegmentsDialog({
  client,
  onConfirm,
  onOpenChange,
  open,
  selectedItems,
}: {
  client: TargetingClient;
  onConfirm: (items: LiverampSegment[]) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  selectedItems: LiverampSegment[];
}) {
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(5);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [draft, setDraft] = React.useState(selectedItems);
  const search = useDebouncedValue(
    searchTerm,
    (nextSearchTerm) => {
      setSearchTerm(nextSearchTerm);
      setPage(0);
    },
    800,
  );
  const query = useQuery({
    queryKey: [...(client.queryScope ?? []), 'targeting', 'liveramp-segments', 'all'],
    queryFn: () => client.listLiverampSegments(liverampLookup),
    enabled: open,
  });
  const paged = pageClientRows({
    items: query.data?.content ?? emptyLiverampSegments,
    searchTerm,
    searchText: liverampSearchText,
    page,
    pageSize: size,
  });
  const pageItems = paged.items;
  const selectedIds = React.useMemo(() => new Set(draft.map((item) => item.id)), [draft]);
  const headerState = loadedSelectionHeaderState(
    selectedIds,
    paged.filteredItems.map((item) => item.id),
  );
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<LiverampSegment>([
        {
          id: 'selected',
          kind: DashboardDataTableColumnKind.Selection,
          checked: (item) => selectedIds.has(item.id),
          label: (item) => item.segmentName || item.id,
          headerChecked: headerState.checked,
          headerDisabled: headerState.disabled,
          headerLabel: dashboardT('common.selectAll'),
          onHeaderChange: (checked) => {
            setDraft((current) =>
              mergeLoadedSelection(
                current,
                paged.filteredItems,
                checked,
                (item) => item.id,
                (item) => item.id,
                (item) => item,
              ),
            );
          },
          onChange: (item, checked) => {
            setDraft((current) =>
              checked
                ? [...current.filter((candidate) => candidate.id !== item.id), item]
                : current.filter((candidate) => candidate.id !== item.id),
            );
          },
        },
        {
          id: 'segmentId',
          header: dashboardT('targeting.liveramp.segmentId'),
          kind: DashboardDataTableColumnKind.Custom,
          headerClassName: 'w-40',
          className: 'w-40 max-w-40',
          cell: (item) => truncatedSegmentValue(item.segmentId || item.id),
        },
        {
          id: 'providerName',
          header: dashboardT('targeting.liveramp.providerName'),
          kind: DashboardDataTableColumnKind.Custom,
          headerClassName: 'w-48',
          className: 'w-48 max-w-48',
          cell: (item) => truncatedSegmentValue(item.providerName),
        },
        {
          id: 'name',
          header: dashboardT('targeting.liveramp.segmentName'),
          kind: DashboardDataTableColumnKind.Custom,
          headerClassName: 'w-64',
          className: 'w-64 max-w-64',
          cell: (item) => truncatedSegmentValue(item.segmentName),
        },
        {
          id: 'segmentDescription',
          header: dashboardT('targeting.liveramp.segmentDescription'),
          kind: DashboardDataTableColumnKind.Custom,
          headerClassName: 'w-64',
          className: 'w-64 max-w-64',
          cell: (item) => truncatedSegmentValue(item.segmentDescription ?? item.description),
        },
        {
          id: 'useRestrictions',
          header: dashboardT('targeting.liveramp.useRestriction'),
          kind: DashboardDataTableColumnKind.Custom,
          headerClassName: 'w-48',
          className: 'w-48 max-w-48',
          cell: (item) => truncatedSegmentValue(item.useRestrictions),
        },
        {
          id: 'permittedAdvertisers',
          header: dashboardT('targeting.liveramp.permittedAdvertisers'),
          kind: DashboardDataTableColumnKind.Custom,
          headerClassName: 'w-48',
          className: 'w-48 max-w-48',
          cell: (item) => truncatedSegmentValue(item.permittedAdvertisers),
        },
        {
          id: 'customSegment',
          header: dashboardT('targeting.liveramp.customSegment'),
          kind: DashboardDataTableColumnKind.Text,
          headerClassName: 'text-center',
          className: 'text-center',
          value: (item) => dashboardT(item.customSegment ? 'common.yes' : 'common.no'),
        },
        {
          id: 'digitalCpm',
          header: dashboardT('targeting.liveramp.digitalCpm'),
          kind: DashboardDataTableColumnKind.Text,
          headerClassName: 'text-right',
          className: 'text-right',
          value: (item) => formatLiverampCpm(item.digitalCpm),
        },
        {
          id: 'tvCpm',
          header: dashboardT('targeting.liveramp.tvCpm'),
          kind: DashboardDataTableColumnKind.Text,
          headerClassName: 'pr-4 text-right',
          className: 'pr-4 text-right',
          value: (item) => formatLiverampCpm(item.tvCpm),
        },
      ]),
    [headerState.checked, headerState.disabled, paged.filteredItems, selectedIds],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DashboardTableDialogContent>
        <DashboardTableDialogHeader
          title={dashboardT('targeting.liveramp.manage')}
          description={dashboardT('targeting.liveramp.manageDescription')}
        />
        <DashboardTableDialogBody>
          <DashboardTableDialogDataTable
            tableLabel={dashboardT('targeting.liveramp.manage')}
            items={pageItems}
            columns={columns}
            getRowKey={(item) => item.id}
            loading={query.isLoading}
            errorMessage={query.isError ? dashboardT('targetingBuilder.segmentsError') : undefined}
            emptyMessage={dashboardT('targetingBuilder.noSegments')}
            minWidthClassName="min-w-[1800px]"
            toolbar={
              <DashboardTableSearchInput
                aria-label={dashboardT('targetingBuilder.searchSegments')}
                placeholder={dashboardT('targetingBuilder.searchSegments')}
                value={search.draft}
                onChange={(event) => {
                  search.setDraft(event.target.value);
                }}
              />
            }
            pagination={{
              value: {
                page: paged.page,
                pageSize: size,
                totalItems: paged.totalItems,
              },
              pageSizes: liverampPageSizes,
              disabled: query.isLoading,
              onChange: (next) => {
                setPage(next.page);
                setSize(next.pageSize);
              },
            }}
          />
        </DashboardTableDialogBody>
        <DashboardTableDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {dashboardT('targeting.liveramp.close')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onConfirm(draft);
              onOpenChange(false);
            }}
          >
            {dashboardT('targeting.liveramp.updateSegments')}
          </Button>
        </DashboardTableDialogFooter>
      </DashboardTableDialogContent>
    </Dialog>
  );
}

function liverampSearchText(item: LiverampSegment) {
  return `${item.segmentName ?? ''}\n${item.providerName ?? ''}`;
}

function truncatedSegmentValue(value: string | null | undefined) {
  const label = value || '-';
  return (
    <span className="block truncate whitespace-nowrap" title={label}>
      {label}
    </span>
  );
}
